/**
 * Plan Diff Utilities
 * 
 * Computes differences between two plan states to show users
 * what changed when they modify Q&A answers.
 * 
 * @see docs/project/specs/dynamic-template-system-v2-spec.md
 */

import type { TemplateStep } from '../types'

export interface PlanSnapshot {
  estimatedHours?: number
  estimatedCostMin?: number
  estimatedCostMax?: number
  steps: Array<TemplateStep>
}

export interface StepChange {
  type: 'added' | 'removed' | 'modified'
  stepSlug: string
  stepTitle: string
  details?: string
}

export interface PlanDiff {
  timeDelta: number // hours added/removed
  costDeltaMin: number
  costDeltaMax: number
  stepsAdded: number
  stepsRemoved: number
  stepsModified: number
  stepChanges: Array<StepChange>
  modifiedStepSlugs: Set<string>
  isEmpty: boolean
  magnitude: 'none' | 'minor' | 'moderate' | 'major'
}

/**
 * Compute the diff between two plan snapshots.
 * 
 * @param before - Plan state before the change
 * @param after - Plan state after the change
 * @returns Diff object describing all changes
 */
export function computePlanDiff(before: PlanSnapshot, after: PlanSnapshot): PlanDiff {
  const stepChanges: Array<StepChange> = []
  const modifiedStepSlugs = new Set<string>()
  
  // Create lookup maps for steps
  const beforeSteps = new Map(before.steps.map(s => [s.slug, s]))
  const afterSteps = new Map(after.steps.map(s => [s.slug, s]))
  
  // Find removed steps
  for (const [slug, step] of beforeSteps) {
    if (!afterSteps.has(slug)) {
      stepChanges.push({
        type: 'removed',
        stepSlug: slug,
        stepTitle: step.title,
      })
    }
  }
  
  // Find added and modified steps
  for (const [slug, afterStep] of afterSteps) {
    const beforeStep = beforeSteps.get(slug)
    
    if (!beforeStep) {
      stepChanges.push({
        type: 'added',
        stepSlug: slug,
        stepTitle: afterStep.title,
      })
      modifiedStepSlugs.add(slug)
    } else {
      // Check for modifications
      const changes = compareSteps(beforeStep, afterStep)
      if (changes.length > 0) {
        stepChanges.push({
          type: 'modified',
          stepSlug: slug,
          stepTitle: afterStep.title,
          details: changes.join(', '),
        })
        modifiedStepSlugs.add(slug)
      }
    }
  }
  
  // Calculate numeric deltas
  const timeDelta = (after.estimatedHours ?? 0) - (before.estimatedHours ?? 0)
  const costDeltaMin = (after.estimatedCostMin ?? 0) - (before.estimatedCostMin ?? 0)
  const costDeltaMax = (after.estimatedCostMax ?? 0) - (before.estimatedCostMax ?? 0)
  
  const stepsAdded = stepChanges.filter(c => c.type === 'added').length
  const stepsRemoved = stepChanges.filter(c => c.type === 'removed').length
  const stepsModified = stepChanges.filter(c => c.type === 'modified').length
  
  const isEmpty = stepChanges.length === 0 && timeDelta === 0 && costDeltaMin === 0 && costDeltaMax === 0
  
  // Determine magnitude
  let magnitude: PlanDiff['magnitude'] = 'none'
  if (!isEmpty) {
    if (stepsAdded + stepsRemoved > 0 || Math.abs(timeDelta) >= 2) {
      magnitude = 'major'
    } else if (stepsModified > 2 || Math.abs(timeDelta) >= 1) {
      magnitude = 'moderate'
    } else {
      magnitude = 'minor'
    }
  }
  
  return {
    timeDelta,
    costDeltaMin,
    costDeltaMax,
    stepsAdded,
    stepsRemoved,
    stepsModified,
    stepChanges,
    modifiedStepSlugs,
    isEmpty,
    magnitude,
  }
}

/**
 * Compare two steps and return a list of what changed.
 */
function compareSteps(before: TemplateStep, after: TemplateStep): Array<string> {
  const changes: Array<string> = []
  
  if (before.estimatedMinutes !== after.estimatedMinutes) {
    const delta = (after.estimatedMinutes ?? 0) - (before.estimatedMinutes ?? 0)
    if (delta > 0) {
      changes.push(`+${delta}min`)
    } else {
      changes.push(`${delta}min`)
    }
  }
  
  // Check for new tips
  const beforeTips = new Set(before.tips ?? [])
  const newTips = (after.tips ?? []).filter(t => !beforeTips.has(t))
  if (newTips.length > 0) {
    changes.push(`+${newTips.length} tip${newTips.length > 1 ? 's' : ''}`)
  }
  
  // Check for new warnings
  const beforeWarnings = new Set(before.warnings ?? [])
  const newWarnings = (after.warnings ?? []).filter(w => !beforeWarnings.has(w))
  if (newWarnings.length > 0) {
    changes.push(`+${newWarnings.length} warning${newWarnings.length > 1 ? 's' : ''}`)
  }
  
  // Check for new tools
  const beforeTools = new Set(before.tools?.map(t => t.name ?? t.customName) ?? [])
  const afterTools = after.tools?.map(t => t.name ?? t.customName) ?? []
  const newTools = afterTools.filter(t => t && !beforeTools.has(t))
  if (newTools.length > 0) {
    changes.push(`+${newTools.length} tool${newTools.length > 1 ? 's' : ''}`)
  }
  
  // Check for new materials
  const beforeMaterials = new Set(before.materials?.map(m => m.name ?? m.customName) ?? [])
  const afterMaterials = after.materials?.map(m => m.name ?? m.customName) ?? []
  const newMaterials = afterMaterials.filter(m => m && !beforeMaterials.has(m))
  if (newMaterials.length > 0) {
    changes.push(`+${newMaterials.length} material${newMaterials.length > 1 ? 's' : ''}`)
  }
  
  return changes
}

/**
 * Format a time delta as a human-readable string.
 */
export function formatTimeDelta(hours: number): string {
  if (hours === 0) return ''
  const sign = hours > 0 ? '+' : ''
  if (Math.abs(hours) < 1) {
    const minutes = Math.round(hours * 60)
    return `${sign}${minutes}min`
  }
  return `${sign}${hours}h`
}

/**
 * Format a cost delta as a human-readable string.
 */
export function formatCostDelta(min: number, max: number): string {
  if (min === 0 && max === 0) return ''
  
  const sign = min > 0 ? '+' : ''
  
  if (min === max) {
    return `${sign}$${Math.abs(min)}`
  }
  
  const avgDelta = (min + max) / 2
  return `${avgDelta > 0 ? '+' : ''}$${Math.round(Math.abs(avgDelta))}`
}

/**
 * Generate a summary message for the diff.
 */
export function generateDiffSummary(diff: PlanDiff): string {
  if (diff.isEmpty) return ''
  
  const parts: Array<string> = []
  
  if (diff.stepsAdded > 0) {
    parts.push(`${diff.stepsAdded} step${diff.stepsAdded > 1 ? 's' : ''} added`)
  }
  
  if (diff.stepsRemoved > 0) {
    parts.push(`${diff.stepsRemoved} step${diff.stepsRemoved > 1 ? 's' : ''} removed`)
  }
  
  if (diff.stepsModified > 0 && diff.stepsAdded === 0 && diff.stepsRemoved === 0) {
    parts.push(`${diff.stepsModified} step${diff.stepsModified > 1 ? 's' : ''} updated`)
  }
  
  const timePart = formatTimeDelta(diff.timeDelta)
  if (timePart) {
    parts.push(`${timePart} time`)
  }
  
  const costPart = formatCostDelta(diff.costDeltaMin, diff.costDeltaMax)
  if (costPart) {
    parts.push(`${costPart} cost`)
  }
  
  if (parts.length === 0) {
    return 'Plan updated'
  }
  
  return parts.join(', ')
}
